/**
 * ProductScannerView — Full-screen product barcode scanning view with its own
 * top overlay (title + hint), camera feed, and bottom controls
 * (mode toggle + manual entry).
 */

import React, { useState, useCallback, useRef } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import BarcodeScanner from "src/components/pages/barcodeScanner/BarcodeScanner";
import ManualEntryPage from "./ManualEntryPage";
import ClassificationProgressScreen from "./ClassificationProgressScreen";
import {
  ScanModeToggle,
  ScanMode,
  ScannerTopOverlay,
  getCameraErrorMessage,
  ScanErrorSheet,
} from "./ScannerSharedComponents";
import { useClassifyItem } from "./useClassifyItem";
import { ClassificationType } from "src/types/classificationTypes";
import PillButton from "src/components/UI/PillButton";
import "src/components/UI/cameraOverlay.scss";
import "./classification.scss";

interface ProductScannerViewProps {
  activeMode: ScanMode;
  onModeChange: (mode: ScanMode) => void;
  title?: string;
}

const ProductScannerView: React.FC<ProductScannerViewProps> = ({
  activeMode,
  onModeChange,
  title = "Waste Profile Classification",
}) => {
  const { classify, cancel, isAnalyzing, scannedAsin } = useClassifyItem();
  const [isScanning, setIsScanning] = useState(true);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [cameraUnavailable, setCameraUnavailable] = useState(false);
  // Separate from scanError so the message stays visible through the sheet's close animation.
  const [sheetOpen, setSheetOpen] = useState(false);

  const dismissScanError = useCallback(() => {
    setSheetOpen(false);
    setIsScanning(true);
  }, []);

  // Stop the scanner on a hard camera failure so onError cannot loop.
  const handleCameraError = useCallback((err: any) => {
    setCameraUnavailable(true);
    setScanError(getCameraErrorMessage(err?.name || "", "product ID"));
    setSheetOpen(true);
    setIsScanning(false);
  }, []);

  const goToManualEntry = useCallback(() => {
    setSheetOpen(false);
    setShowManualEntry(true);
  }, []);

  const handleSheetClose = useCallback(() => {
    if (cameraUnavailable) {
      setSheetOpen(false);
    } else {
      dismissScanError();
    }
  }, [cameraUnavailable, dismissScanError]);
  // Prevents multiple simultaneous scans from being processed
  const isProcessingRef = useRef(false);

  const handleScan = useCallback(
    async (result: any) => {
      if (!result || result.length === 0) return;
      if (isProcessingRef.current) return;

      const asin = result[0]?.rawValue?.trim();
      if (!asin) return;

      isProcessingRef.current = true;
      setScanError(null);
      setIsScanning(false);
      try {
        await classify(asin);
      } catch {
        setCameraUnavailable(false);
        setScanError(
          "We couldn't identify this product. Make sure the barcode is clear, or try another product.",
        );
        setSheetOpen(true);
        setIsScanning(true);
      } finally {
        isProcessingRef.current = false;
      }
    },
    [classify],
  );

  if (isAnalyzing) {
    return (
      <ClassificationProgressScreen
        classificationConfig={{
          type: ClassificationType.SDS,
          asin: scannedAsin,
        }}
        onBack={cancel}
      />
    );
  }

  if (showManualEntry) {
    return (
      <ManualEntryPage
        onSwitchToScanner={() => {
          setShowManualEntry(false);
          setCameraUnavailable(false);
          setScanError(null);
          setSheetOpen(false);
          setIsScanning(true);
        }}
      />
    );
  }

  return (
    <Column
      className="barcode-scanner-viewport pwa-fullscreen-viewport"
      backgroundColor="#3a3a3a"
      height="100vh"
      spacing="none"
    >
      {/* Top overlay */}
      <ScannerTopOverlay title={title}>
        <Row
          className="camera-pill-bg"
          width="300px"
          alignmentHorizontal="center"
          spacingInset="300"
        >
          <Text color="inverted">Scan the product barcode</Text>
        </Row>
      </ScannerTopOverlay>

      {/* Camera feed */}
      <BarcodeScanner isScanning={isScanning} onScan={handleScan} onError={handleCameraError} />

      {/* Bottom overlay */}
      <Column
        className="camera-overlay camera-overlay--bottom"
        spacingInset="450"
        alignmentHorizontal="center"
        spacing="400"
      >
        <ScanModeToggle activeMode={activeMode} onModeChange={onModeChange} />
        <PillButton
          text="Having Trouble? Try manual Entry"
          color="glass"
          onClick={() => setShowManualEntry(true)}
        />
      </Column>

      {/* Scan error / camera-unavailable pop-up */}
      <ScanErrorSheet
        open={sheetOpen}
        message={scanError}
        cameraUnavailable={cameraUnavailable}
        onClose={handleSheetClose}
        onManualEntry={goToManualEntry}
        onScanAnother={dismissScanError}
        manualEntryLabel="ENTER PRODUCT ID MANUALLY"
        scanAnotherLabel="SCAN ANOTHER PRODUCT / BARCODE"
      />
    </Column>
  );
};

export default ProductScannerView;
